import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { View } from '../../types';
import { ShelterDashboard } from './ShelterDashboard';
import { Capacitor } from '@capacitor/core';
import { LoginView, RegisterView } from '../../components/AuthViews';
import { ShelterHome } from './ShelterHome';
import {
    Heart, ArrowLeft,
    Zap, CheckCircle2, ArrowRight, Home, MapPin,
    Activity, Mail, LogOut,
    Users, Calendar, ChevronRight, Award, Plus, FileText
} from 'lucide-react';

interface ShelterPortalViewProps {
    onNavigate: (view: View) => void;
}

export const ShelterPortalView: React.FC<ShelterPortalViewProps> = ({ onNavigate }) => {
    const { user, logout } = useAuth();
    // En la web, quien entra a /albergues sin sesión ve primero la portada
    // (como ya pasa en /criaderos); en la APK nativa de albergue se va
    // directo al ingreso. Antes esto siempre caía en 'login' en la web,
    // sin mostrar nunca la portada.
    const [activePortalTab, setActivePortalTab] = useState<'discover' | 'login' | 'register'>(Capacitor.isNativePlatform() ? 'login' : 'discover');

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const action = params.get('action');
        if (action === 'login') {
            setActivePortalTab('login');
        } else if (action === 'register' || params.get('role') === 'shelter') {
            setActivePortalTab('register');
        }
    }, []);

    const handleInnerNavigate = (view: View) => {
        if (view === View.REGISTER) {
            setActivePortalTab('register');
        } else if (view === View.LOGIN) {
            setActivePortalTab('login');
        } else if (view === View.LANDING || view === View.SHELTER_DASHBOARD) {
            setActivePortalTab('discover');
        } else {
            onNavigate(view);
        }
    };

    const handleLogout = () => {
        if (confirm("¿Estás seguro de que quieres cerrar sesión del Portal de Albergues?")) {
            logout();
            setActivePortalTab('login');
        }
    };

    // Albergue por rol propio o por solicitud de socio aprobada (acceso multi-portal,
    // misma cuenta que su panel de usuario normal — ver BreederPortalView.tsx).
    const isShelter = !!user && (user.role?.includes('shelter') || (user as any).partner_roles?.includes('shelter') || user.role?.includes('root'));

    // CASE 1: Shelter is Logged In
    if (user && isShelter) {
        return (
            <div className="min-h-screen bg-slate-50 w-full animate-in fade-in duration-500">
                <ShelterDashboard onExit={handleLogout} />
            </div>
        );
    }

    // CASE 2 eliminado: un usuario logueado SIN el rol de albergue ya no ve una
    // pantalla de "acceso restringido" — cae directo al flujo normal de invitado
    // (CASE 3), donde puede registrarse como albergue (eso crea una solicitud de
    // socio para la MISMA cuenta, no una cuenta nueva) o iniciar sesión con otra cuenta.

    // CASE 3: Guest / Not Logged In
    return (
        <div className="bg-white min-h-screen overflow-x-hidden font-sans selection:bg-rose-500 selection:text-white">
            {/* Top Branded Navigation Header */}
            <nav className="fixed top-0 left-0 w-full bg-white/70 backdrop-blur-xl z-50 border-b border-rose-100/60 px-6 py-4 pt-[calc(1rem+env(safe-area-inset-top))]">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <button 
                        onClick={() => {
                            setActivePortalTab('discover');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }} 
                        className="flex items-center gap-2.5 group"
                    >
                        <div className="bg-gradient-to-br from-[#ff6b3d] to-[#f43f74] p-2.5 rounded-2xl text-white group-hover:rotate-12 transition-transform shadow-lg shadow-rose-200">
                            <Heart size={20} className="fill-white" />
                        </div>
                        <div className="text-left">
                            <span className="font-bold text-[#3b2430] text-sm sm:text-base leading-none block whitespace-nowrap" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                                <span className="sm:hidden">Albergues</span><span className="hidden sm:inline">Amigo Peludo Albergues</span>
                            </span>
                            <span className="hidden sm:block text-[8px] font-bold text-rose-500 tracking-[0.25em] uppercase mt-0.5">Rescate · Cuidado · Hogar</span>
                        </div>
                    </button>

                    {/* Navigation Actions — visible en todos los tamaños */}
                    <div className="flex items-center gap-1.5 sm:gap-2">
                        <button
                            onClick={() => setActivePortalTab('discover')}
                            className={`hidden sm:flex px-3 md:px-4 py-2 md:py-2.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'discover'
                                    ? 'bg-rose-50 text-rose-600'
                                    : 'text-slate-500 hover:text-slate-800'
                            }`}
                        >
                            Descubrir
                        </button>
                        <button
                            onClick={() => setActivePortalTab('login')}
                            className={`px-3 sm:px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'login'
                                    ? 'bg-gradient-to-r from-[#ff6b3d] to-[#f43f74] text-white shadow-lg shadow-rose-200'
                                    : 'border border-rose-100 text-[#5b3a46] hover:bg-rose-50'
                            }`}
                        >
                            Ingresar
                        </button>
                        <button
                            onClick={() => setActivePortalTab('register')}
                            className={`px-3 sm:px-4 md:px-5 py-2 md:py-2.5 rounded-xl font-black text-[10px] md:text-xs uppercase tracking-widest transition-all ${
                                activePortalTab === 'register'
                                    ? 'bg-gradient-to-r from-[#ff6b3d] to-[#f43f74] text-white'
                                    : 'bg-[#3b2430] text-white hover:bg-rose-500'
                            }`}
                        >
                            <span className="hidden sm:inline">Crear </span>Cuenta
                        </button>
                    </div>
                </div>
            </nav>

            {/* Portada (Descubrir): ocupa todo el ancho, con su propio hero bajo la barra fija */}
            {activePortalTab === 'discover' && (
                <ShelterHome
                    onRegister={() => { setActivePortalTab('register'); window.scrollTo({ top: 0 }); }}
                    onLogin={() => { setActivePortalTab('login'); window.scrollTo({ top: 0 }); }}
                />
            )}

            {/* Ingreso y registro */}
            <div className={`${activePortalTab === 'discover' ? 'hidden' : ''} pt-24 md:pt-28 pb-20 max-w-7xl mx-auto px-6 animate-in fade-in duration-300`}>
                {activePortalTab === 'login' && (
                    <div className="max-w-md mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <LoginView 
                            initialMode="SHELTER" 
                            hideRegister={true} 
                            onNavigate={handleInnerNavigate} 
                        />
                    </div>
                )}

                {activePortalTab === 'register' && (
                    <div className="max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <RegisterView 
                            role="shelter" 
                            onNavigate={handleInnerNavigate} 
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
export default ShelterPortalView;
